import React, { useState, useEffect, useRef, useCallback } from 'react'

import { Formik } from 'formik';
import { useSelector, useDispatch } from 'react-redux'
import * as yup from 'yup';

import { getReport, cleanReport } from '../../../store/reports/reports.actions';
import { getAllCustomers, getCustomerDetails } from '../../../store/customers/customers.actions';

// Components

import Button from '../../UI/Button/Button';
import SelectInput from '../../UI/FormInput/SelectInput';
import DownloadReportButton from '../../UI/DownloadReportButton/DownloadReportButton';
import Loader from '../../UI/Loader/Loader';

// Constants

import { REPORT_TYPES } from '../../../config/constants';

function CustomerWatcher({ customerId, setFieldValue, onCustomerChange }) {
  const prevRef = useRef(customerId);

  useEffect(() => {
    if (customerId !== prevRef.current) {
      prevRef.current = customerId;
      setFieldValue('start_month', '');
      setFieldValue('end_month', '');
      setFieldValue('balances', []);
      onCustomerChange(customerId || null);
    }
  }, [customerId, setFieldValue, onCustomerChange]);

  return null;
}

export default function ReportBalanceWithBalance({setShowBalanceWithBalanceReportModal}) {
  const [reportUrl, setReportUrl] = useState(null);
  const [monthOptions, setMonthOptions] = useState([]);
  const formikRef = useRef(null);
  const reports = useSelector((state) => state.reports.reports);
  const customers = useSelector((state) => state.customers.customersAll);
  const customerDetail = useSelector((state) => state.customers.customerDetail);
  const dispatch = useDispatch();

  const handleCustomerChange = useCallback((customerId) => {
    if (customerId) {
      dispatch(getCustomerDetails(customerId));
    } else {
      setMonthOptions([]);
    }
  }, [dispatch]);

  useEffect(() => {
    const checkBalances = customerDetail?.data?.check_balances;
    if (checkBalances?.length > 0) {
      const maxLen = Math.max(...checkBalances.map(o => o.balances?.length || 0));
      const longestAccount = checkBalances.find(x => (x.balances?.length || 0) === maxLen);
      const balances = longestAccount?.balances || [];
      setMonthOptions(balances.map(b => ({ value: b.title, label: b.title })));
      formikRef.current?.setFieldValue('balances', balances);
    } else if (customerDetail?.data) {
      setMonthOptions([]);
      formikRef.current?.setFieldValue('balances', []);
    }
  }, [customerDetail]);

  // Generate Report

  const generateBalanceWithBalanceReport = async (values, actions) => {
    setReportUrl(null);

    const balances = values.balances || [];
    const startIdx = values.start_month ? balances.map(b => b.title).indexOf(values.start_month) : null;
    const endIdx = values.end_month ? balances.map(b => b.title).indexOf(values.end_month) : null;
    const endIdxInclusive = endIdx !== null ? endIdx + 1 : null;

    let months = [];
    if (startIdx !== null && endIdxInclusive !== null) {
      months = balances.slice(startIdx, endIdxInclusive).map(b => b.title);
    }

    const data = {
      customer_id: values.customer_id ? values.customer_id : null,
      months,
      export: true,
    }

    dispatch(getReport(data, REPORT_TYPES.balancewithbalance));
  }


  // Set up report download url

  useEffect(() => {
    if (reports.data) {
      const url = window.URL.createObjectURL(new Blob([reports.data]));
      setReportUrl(url);
    }
  }, [reports]);


  // Cleanup the storage on load

  useEffect(() => {
    dispatch(cleanReport());
    dispatch(getAllCustomers())
  }, [dispatch])

  return (
    <>

      {(customers?.isLoading || customers?.error) &&
        <Loader
          message={'Cargando...'}
          error={customers.error}
          action={() => dispatch(getAllCustomers())}
          dark={true}
      />}

    {!customers?.isLoading && customers?.data &&
      <>
        <Formik
            innerRef={formikRef}
            initialValues={{
              start_month: '',
              end_month: '',
              balances: [],
              customer_id: ''
            }}
            validationSchema={validationSchema}
            onSubmit={(values, actions) => generateBalanceWithBalanceReport(values, actions)}
          >

            {props => (
            <>

              {/* Form Inputs */}

              <>

                <CustomerWatcher
                  customerId={props.values.customer_id}
                  setFieldValue={props.setFieldValue}
                  onCustomerChange={handleCustomerChange}
                />

                <SelectInput
                  formik={props}
                  fieldName={'customer_id'}
                  label={'Cliente'}
                  placeholder={'Seleccione'}
                  options={customers?.data ? customers?.data.map(option => ({ value: option.id, label: option.name })) : []}
                />

                {customerDetail?.isLoading &&
                  <Loader
                    message={'Cargando meses...'}
                    dark={true}
                  />}

                {!customerDetail?.isLoading &&
                  <>
                    <SelectInput
                      formik={props}
                      fieldName={'start_month'}
                      label={'Mes inicio'}
                      placeholder={'Seleccione'}
                      options={monthOptions}
                      disabled={!props.values.customer_id || monthOptions.length === 0}
                    />

                    <SelectInput
                      formik={props}
                      fieldName={'end_month'}
                      label={'Mes fin'}
                      placeholder={'Seleccione'}
                      options={monthOptions}
                      disabled={!props.values.customer_id || monthOptions.length === 0}
                    />
                  </>}

                {/* Loader */}

                {(reports?.isLoading || reports?.error) &&
                  <Loader
                    message={'Generando reporte...'}
                    error={reports.error}
                    action={() => getReport(props.values, REPORT_TYPES.balancewithbalance)}
                    dark={true}
                />}

                {/* Download Report Button */}

                {!reports?.isLoading && reports.data && reportUrl &&
                <DownloadReportButton
                  fileUrl={reportUrl}
                  fileName={'reporte-data-plana-balances'}
                />}


                {/* Form actions */}

                <div className='form-actions'>

                  <Button
                    action={() => setShowBalanceWithBalanceReportModal(false)}
                    label={'Cancelar'}
                    secondaryStyle={true}
                    disabled={reports?.isLoading}
                  />

                  <Button
                    action={props.handleSubmit}
                    label={'Generar'}
                    disabled={reports?.isLoading}
                    isLoading={reports?.isLoading}
                  />

                </div>
              </>

            </>)}

        </Formik>

      </>}
    </>
  )
}

// Form validation schema

const validationSchema = yup.object().shape({
  customer_id: yup.string().required('Este campo es obligatorio'),
  start_month: yup.string().when('end_month', {
    is: (val) => val !== undefined && val !== '',
    then: yup.string().required('Este campo es requerido'),
    otherwise: yup.string(),
  }),
  end_month: yup.string().test('endMonthTest', 'Mes fin no puede ser menor al mes de inicio', function (value) {
    const balances = this?.parent?.balances;
    const startMonth = this?.parent?.start_month;
    if (!startMonth || !value || !balances?.length) return true;
    const startIdx = balances.map(b => b.title).indexOf(startMonth);
    const endIdx = balances.map(b => b.title).indexOf(value);
    return startIdx <= endIdx;
  }),
  balances: yup.array(),
});
